/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Settings · Team: the agency's seats, and who is in them.
 *
 * A card per plan, because the plan that pays for a seat is what sizes the
 * allowance of whoever sits in it — a single list would put two people with
 * different ceilings side by side with nothing to say why.
 *
 * The rows are one grid, not one layout each, so the avatars, the meters and
 * the menus line up down the whole card.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { Text, TextInput, UnstyledButton } from '@/shared/ui';
import { useMediaQuery } from '@/shared/hooks/use-media-query';
import { useAgency } from '@pages/authenticated/agency-pages/AgenciesContext';
import { useAgencyPermissions } from '@/features/agency';
import { AgenciesService } from '@/services/requests/housien-services/AgenciesService';
import type { InviteEntry } from '@/services/requests/housien-services/AgenciesService.team';
import type { PlanFeatureType } from '@/features/pricing';
import { loadPlans } from '@/features/onboarding/hooks/plans-cache';
import { InviteTeam } from '@/features/onboarding/components/sections/InviteTeam';
import { planTitleFor } from '@/features/onboarding/components/sections/InviteTeam.rows';
import { AdaptiveFormShell } from '@/shared/ui/components/form/AdaptiveFormShell';
import {
  AddSeatButton, CARD, DriveIcon, FreeSeatsToggle, Meter, PlanPill,
} from './TeamScreen.parts';
import { OwnerSeatPick } from '@/features/subscription/components/seats/OwnerSeatPick';
import { PendingRow, PersonRow, VacantRow } from './TeamScreen.rows';
import { RowMenu } from './TeamScreen.menu';
import { AddSeatModal } from './AddSeatModal';
import { copyToClipboard } from './invite-actions';
import { driveTone, foldFreeSeats, teamGroups } from './team-rows';
import { useTeamLoad } from './useTeamLoad';
import { TeamLoadError, TeamLoading } from './TeamScreen.loading';

const GB = 1024 ** 3;

/** A byte count as the card says it: one decimal, in gigabytes. */
// "12.3" but never "0.0" or "1024.0": a dead decimal reads as fake precision.
/**
 * As gb.
 */
const asGb = (bytes: number): string => {
  const gb = bytes / GB;
  return Number.isInteger(Number(gb.toFixed(1))) ? String(Math.round(gb)) : gb.toFixed(1);
};

/** Which row has its menu open. */
type OpenMenu = { kind: 'person' | 'pending'; key: string } | null;

/**
 * The team screen.
 *
 * @returns The seats, the people in them, and the drive they share.
 */
export const TeamScreen: React.FC = () => {
  const { t } = useTranslation('features/agency-settings');
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 639px)');
  const agency = useAgency((state) => state.agency);
  const { canInviteMember, canEditMemberRole, userEmail } = useAgencyPermissions();
  const { team, phase, reload } = useTeamLoad(agency?.id);
  const [catalogue, setCatalogue] = useState<PlanFeatureType[]>([]);
  const [search, setSearch] = useState('');
  const [menu, setMenu] = useState<OpenMenu>(null);
  const [invitingPlan, setInvitingPlan] = useState<string | null | false>(false);
  const [addingSeatPlan, setAddingSeatPlan] = useState<string | null>(null);
  // The cards whose spare seats the reader asked to see in full. A card is
  // folded until then: forty identical free rows push the next plan off the
  // screen.
  const [unfolded, setUnfolded] = useState<ReadonlySet<string>>(new Set());
  const toggleSeats = (planId: string) => setUnfolded((current) => {
    const next = new Set(current);
    if (!next.delete(planId)) next.add(planId);
    return next;
  });
  useEffect(() => {
    loadPlans().then((plans) => setCatalogue(plans.plans_features)).catch(() => undefined);
  }, []);

  const planTitle = useCallback(
    (planId: string) => planTitleFor(catalogue, planId),
    [catalogue],
  );

  const groups = useMemo(() => teamGroups(team, search, catalogue), [team, search, catalogue]);
  const blocks = team?.seats.by_plan ?? [];
  // The buyer picks which bought seat is theirs; until they do, the card
  // stays. Only the owner sees it — the choice is theirs, not an admin's.
  const owner = (team?.people ?? []).find((person) => person.role === 'owner') ?? null;
  const ownerUnseated = Boolean(
    owner && !owner.plan_id
    && (owner.email || '').toLowerCase() === (userEmail || '').toLowerCase(),
  );
  // The reader, not the agency. agency.email is the company's contact
  // address and is often not set at all, so every row read "Ativo" and
  // nobody was ever marked as themselves.
  const you = (userEmail || '').toLowerCase();

  const act = async (run: () => Promise<unknown>, okKey: string) => {
    setMenu(null);
    try {
      await run();
      toast.success(t(okKey));
      await reload();
    } catch {
      toast.error(t('v2.team.actionFailed'));
    }
  };

  const changeRole = (email: string, role: string) => act(
    () => AgenciesService.changeUserRole(agency.id ?? '', {
      user_email: email, role: role as 'admin' | 'broker' | 'operational',
    }),
    'v2.team.roleChanged',
  );

  const move = (email: string, planId: string) => act(async () => {
    const result = await AgenciesService.moveMemberSeat(agency.id ?? '', email, planId);
    // The server refuses a move the screen thought was fine when the seat
    // went between loading and clicking. That is not an error to swallow.
    if (!result.ok) throw new Error(result.reason ?? 'refused');
  }, 'v2.team.seatMoved');

  const removePerson = (email: string) => act(
    () => AgenciesService.removeMemberFromAgency(agency.id ?? '', email),
    'v2.team.memberRemoved',
  );

  const cancelInvite = (id: string | null) => act(
    () => AgenciesService.revokeInvitation(agency.id ?? '', id ?? ''),
    'v2.team.inviteCancelled',
  );

  /**
   * Sends the invitation again, with a token that works.
   *
   * No reload: nothing on the row changes. The invitation was pending and
   * still is; what moved is its expiry, which the row does not show.
   */
  const resendInvite = async (id: string | null) => {
    setMenu(null);
    try {
      await AgenciesService.resendInvitation(agency.id ?? '', id ?? '');
      toast.success(t('v2.team.inviteResent'));
    } catch {
      // Said out loud rather than swallowed: the operator is about to tell
      // somebody they sent it again.
      toast.error(t('v2.team.actionFailed'));
    }
  };

  /**
   * Puts the invitation's link on the clipboard.
   *
   * The expiry goes in the message because the link is short-lived and the
   * operator is about to paste it somewhere that will not say so.
   */
  const copyInviteLink = async (id: string | null) => {
    setMenu(null);
    try {
      const { url } = await AgenciesService.invitationLink(agency.id ?? '', id ?? '');
      if (!url || !(await copyToClipboard(url))) throw new Error('not copied');
      toast.success(t('v2.team.inviteLinkCopied'));
    } catch {
      toast.error(t('v2.team.actionFailed'));
    }
  };

  const sendInvites = async (entries: InviteEntry[]) => {
    const result = await AgenciesService.inviteAgencyMembers(agency.id ?? '', entries);
    if (result.sent) {
      toast.success(t('v2.team.invitesSent', { count: result.sent }));
      await reload();
    }
    if (result.sent === entries.length) setInvitingPlan(false);
    return result.results;
  };

  // A team that has not arrived is not a team of zero. The zeros below are
  // only drawn once the response is in hand.
  if (team == null) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        {phase === 'error'
          ? <TeamLoadError onRetry={() => { void reload(); }} />
          : <TeamLoading />}
      </div>
    );
  }

  const counts = {
    people: team.people.length,
    pending: team.pending.length,
    taken: team.seats.taken,
    bought: team.seats.bought ?? 0,
  };
  const drive = team?.storage;
  const driveFill = drive?.used_bytes != null && drive.total_gb
    ? Math.min(1, drive.used_bytes / (drive.total_gb * GB))
    : null;

  const modalBlocks = invitingPlan === false
    ? []
    : blocks.filter((block) => invitingPlan === null || block.plan_id === invitingPlan);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      {addingSeatPlan && (
        <AddSeatModal
          agencyId={agency?.id ?? ''}
          initialPlanId={addingSeatPlan}
          blocks={blocks}
          plans={catalogue}
          planTitle={planTitle}
          onClose={() => setAddingSeatPlan(null)}
          onAdded={() => reload()}
        />
      )}
      {ownerUnseated && (
        <OwnerSeatPick
          blocks={blocks}
          context="team"
          planTitle={planTitle}
          onChoose={async (planId) => {
            await move(owner?.email ?? '', planId);
            return true;
          }}
        />
      )}
      <div
        style={{
          display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
          gap: 'var(--space-3) var(--space-4)', flexWrap: 'wrap',
        }}
      >
        <div
          style={{
            flex: '1 1 20rem', minWidth: 0, display: 'flex',
            flexDirection: 'column', gap: 'var(--space-0-5)',
          }}
        >
          {/* No heading here: the settings shell already titles the section
              "Equipe" right above — two of them read as a stutter. */}
          <Text
            style={{
              fontSize: 'var(--text-small)', lineHeight: 1.5,
              color: 'var(--text-tertiary-soft)',
            }}
          >
            {[
              t('v2.team.captionPeople', { count: counts.people }),
              t('v2.team.captionPending', { count: counts.pending }),
              `${t('v2.team.captionSeats', counts)}. ${t('v2.team.captionQuota')}`,
            ].join(' · ')}
          </Text>
        </div>
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
            flex: isMobile ? '1 1 100%' : '0 1 22rem',
          }}
        >
          <TextInput
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('v2.team.searchPlaceholder')}
            aria-label={t('v2.team.searchPlaceholder')}
            style={{ flex: 1, minWidth: 0 }}
          />
          {canInviteMember && (
            <UnstyledButton
              onClick={() => setInvitingPlan(null)}
              data-testid="team-invite"
              style={{
                flex: 'none', height: 32, padding: '0 var(--space-3-5)',
                border: '1px solid var(--border-medium)', borderRadius: 'var(--radius-lg)',
                background: 'var(--surface-primary)', cursor: 'pointer',
                fontSize: 'var(--text-small)', fontWeight: 'var(--font-semibold)',
                color: 'var(--text-primary)', whiteSpace: 'nowrap',
              }}
            >
              {t('v2.team.inviteAction')}
            </UnstyledButton>
          )}
        </div>
      </div>

      {drive && drive.total_gb != null && (
        <div
          data-testid="team-storage"
          style={{
            ...CARD, flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap',
            gap: 'var(--space-4)', padding: 'var(--space-3-5) var(--space-5)',
            color: 'var(--seed-base)',
          }}
        >
          <div
            style={{
              display: 'flex', alignItems: 'center', gap: 'var(--space-2-5)',
              flex: '1 1 16rem', minWidth: 0,
            }}
          >
            <DriveIcon />
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <Text
                component="span"
                style={{
                  fontSize: 'var(--text-sm)', fontWeight: 'var(--font-bold)',
                  color: 'var(--text-primary)',
                }}
              >
                {t('v2.team.storageTitle')}
              </Text>
              <Text
                component="span"
                style={{
                  fontSize: 'var(--text-meta)', lineHeight: 1.4,
                  color: 'var(--text-tertiary-soft)',
                }}
              >
                {t('v2.team.storageCaption')}
              </Text>
            </div>
          </div>
          <div
            style={{
              display: 'flex', flexDirection: 'column', gap: 'var(--space-1-5)',
              flex: '0 1 280px', minWidth: 200,
            }}
          >
            <div
              style={{
                display: 'flex', justifyContent: 'space-between',
                gap: 'var(--space-2)', fontSize: 'var(--text-small)',
              }}
            >
              <Text
                component="span"
                style={{ fontWeight: 'var(--font-bold)', color: 'var(--text-primary)' }}
              >
                {drive.used_bytes == null
                  ? t('v2.team.storageUnknown')
                  : t('v2.team.storageUsed', {
                    used: asGb(drive.used_bytes), total: drive.total_gb,
                  })}
              </Text>
              {drive.used_bytes != null && (
                <Text component="span" style={{ color: 'var(--text-tertiary-soft)' }}>
                  {t('v2.team.storageFree', {
                    free: asGb(Math.max(0, drive.total_gb * GB - drive.used_bytes)),
                  })}
                </Text>
              )}
            </div>
            <Meter
              fill={driveFill}
              tone={driveTone(drive.used_bytes, drive.total_gb)}
              height={6}
            />
          </div>
        </div>
      )}

      {groups.map((group) => {
        const folded = foldFreeSeats(group.rows, unfolded.has(group.planId));
        return (
          <div key={group.planId} style={CARD} data-testid={`team-plan-group-${group.planId}`}>
            <div
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 'var(--space-3)', flexWrap: 'wrap',
                padding: 'var(--space-3) var(--space-5)',
                borderBottom: '1px solid var(--border-ultra-subtle)',
              }}
            >
              <div
                style={{
                  display: 'flex', alignItems: 'center', gap: 'var(--space-2-5)',
                  flexWrap: 'wrap', minWidth: 0,
                }}
              >
                <PlanPill label={planTitle(group.planId) ?? t('v2.team.planUnknown')} />
                <Text
                  component="span"
                  style={{
                    fontSize: 'var(--text-small)', fontWeight: 'var(--font-semibold)',
                    color: 'var(--text-primary)',
                  }}
                >
                  {t('v2.team.seatsUsedOf', {
                    used: group.bought - group.free, total: group.bought,
                  })}
                </Text>
              </div>
              <AddSeatButton
                planId={group.planId}
                onClick={() => setAddingSeatPlan(group.planId)}
              />
            </div>

            {group.rows.length === 0 && (
              <Text
                style={{
                  padding: 'var(--space-4) var(--space-5)',
                  fontSize: 'var(--text-small)', color: 'var(--text-tertiary-soft)',
                }}
              >
                {t('v2.team.nobodyFound')}
              </Text>
            )}

            {folded.rows.map((row, index) => {
              const last = index === folded.rows.length - 1 && !folded.foldable;
              if (row.kind === 'vacant') {
                return (
                  <VacantRow
                    key={`vacant-${row.seatId ?? index}`}
                    planId={group.planId}
                    index={index}
                    last={last}
                    mobile={isMobile}
                    onInvite={() => setInvitingPlan(group.planId)}
                  />
                );
              }
              if (row.kind === 'pending') {
                const open = menu?.kind === 'pending' && menu.key === row.invite.email;
                return (
                  <PendingRow
                    key={`pending-${row.invite.email}`}
                    invite={row.invite}
                    roleLabel={t(`v2.roles.${row.invite.role ?? 'broker'}`)}
                    last={last}
                    mobile={isMobile}
                    onMenu={canEditMemberRole
                      ? () => setMenu(open ? null : { kind: 'pending', key: row.invite.email })
                      : undefined}
                  >
                    {open && (
                      <RowMenu
                        currentPlanId={row.invite.plan_id}
                        currentRole={row.invite.role}
                        blocks={blocks}
                        planTitle={planTitle}
                        pending
                        canInvite={canInviteMember}
                        onRole={(role) => changeRole(row.invite.email, role)}
                        onMove={() => undefined}
                        onResend={() => resendInvite(row.invite.id)}
                        onCopyLink={() => copyInviteLink(row.invite.id)}
                        onRemove={() => cancelInvite(row.invite.id)}
                        onClose={() => setMenu(null)}
                      />
                    )}
                  </PendingRow>
                );
              }
              const open = menu?.kind === 'person' && menu.key === row.person.email;
              return (
                <PersonRow
                  key={row.person.email}
                  person={row.person}
                  roleLabel={t(`v2.roles.${row.person.role ?? 'broker'}`)}
                  isYou={row.person.email === you}
                  last={last}
                  mobile={isMobile}
                  onMenu={canEditMemberRole
                    ? () => setMenu(open ? null : { kind: 'person', key: row.person.email })
                    : undefined}
                >
                  {open && (
                    <RowMenu
                      currentPlanId={row.person.plan_id}
                      currentRole={row.person.role}
                      blocks={blocks}
                      planTitle={planTitle}
                      pending={false}
                      onRole={(role) => changeRole(row.person.email, role)}
                      onMove={(planId) => move(row.person.email, planId)}
                      onRemove={() => removePerson(row.person.email)}
                      onClose={() => setMenu(null)}
                    />
                  )}
                </PersonRow>
              );
            })}

            {folded.foldable && (
              <FreeSeatsToggle
                planId={group.planId}
                hidden={folded.hidden}
                onClick={() => toggleSeats(group.planId)}
              />
            )}
          </div>
        );
      })}

      <Text
        style={{
          fontSize: 'var(--text-meta)', lineHeight: 1.5,
          color: 'var(--text-tertiary-soft)',
        }}
      >
        {t('v2.team.footerNote')}
      </Text>

      {invitingPlan !== false && (
        <AdaptiveFormShell
          open
          density="compact"
          onClose={() => setInvitingPlan(false)}
          title={invitingPlan
            ? t('v2.team.inviteToPlan', {
              plan: planTitle(invitingPlan) ?? t('v2.team.planUnknown'),
            })
            : t('v2.team.inviteToTeam')}
        >
          <InviteTeam
            compact
            blocks={modalBlocks}
            planTitle={planTitle}
            seatedEmails={[
              ...(team?.people ?? []).map((person) => person.email),
              ...(team?.pending ?? []).map((invite) => invite.email),
            ]}
            onSend={sendInvites}
            onSkip={() => setInvitingPlan(false)}
          />
        </AdaptiveFormShell>
      )}
    </div>
  );
};

export default TeamScreen;
